import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ClientRegistrationFormComponent from '@/components/CommercialPage/ClientRegistrationForm/ClientRegistrationFormComponent';
import { useUser } from '@/contexts/UserContext';
import { parseInputName } from '@/helpers';
import { masksByCountry } from '@/helpers/mask';
import { useAddress } from '@/hooks/useAddress';
import { useClients } from '@/hooks/useClients';
import { useFranchisees } from '@/hooks/useFranchisees';
import AddressService from '@/services/addressService';
import { showToast } from '@/store/ducks/toasts';

const CLASSIFICATIONS = [
  'IMOBILIARIA',
  'CORRETOR_DE_IMOVEIS',
  'PROPRIETARIO',
  'CONDOMINIO',
  'ADMINISTRADORA_DE_CONDOMINIOS',
  'SINDICO_PROFISSIONAL',
  'CONSTRUTORA_INCORPORADORA',
  'EMPREITEIRA',
  'INSTITUICAO_FINANCEIRA',
  'REDE_DE_VAREJO',
  'PESSOA_FISICA',
  'OUTROS',
];

const ORIGINS = ['SA', 'UNIDADE', 'FRANQUIA'];

const ClientRegistrationFormContainer = () => {
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState('pj');
  const [client, setClient] = useState({ 
    client_type: 'pj', 
    contact: { assignment_email_rule: 'CREATOR' },
    address: {},
    billing_address: {}
  });

  const [showBillingAddress, setShowBillingAddress] = useState(false);
  const [selectedFranchisee, setSelectedFranchisee] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();
  const me = useUser();

  const country = me?.profile?.country || 'BR';
  const { useStates, useCities, useNeighborhoods, useLocation, validateZipcode, getZipcodeMask } = useAddress(country);
  const { useCreateClientMutation } = useClients();
  const { useFranchiseesList } = useFranchisees();

  const { data: franchiseesData = [], isLoading: loadingFranchisees } = useFranchiseesList();
  const { data: states = [] } = useStates();
  
  const createMutation = useCreateClientMutation();

  const franchisees = useMemo(() => {
    return franchiseesData.map(f => ({ value: f.id, label: f.name }));
  }, [franchiseesData]);

  const useTextInputForAddress = ['UY', 'PY', 'MX', 'CO'].includes(country);

  const masks = useMemo(() => ({
    zipcode: getZipcodeMask(),
    cnpj: masksByCountry[country].CNPJ_MASK,
    cpf: masksByCountry[country].CPF_ONLY_MASK,
    phone: masksByCountry[country].PHONE_MASK,
    cellphone: masksByCountry[country].CELLPHONE_MASK,
    cellphoneLength: masksByCountry[country].CELLPHONE_MASK_LENGTH,
  }), [country, getZipcodeMask]);

  const handleChange = useCallback(async evt => {
    const { name } = evt.target;
    let { value } = evt.target;
    const parsed = parseInputName(name, value);
    const parent = Object.keys(parsed)[0];

    if (name === 'client_type') setType(value);

    if (name === 'showBillingAddress') {
      if (value === 'true') {
        const state = { ...client };
        delete state.billing_address;
        setClient(state);
      }
      setShowBillingAddress(value === 'true');
      return;
    }

    // Auto-fill address by zipcode
    if (name.includes('zipcode') && validateZipcode(value)) {
      // We'll handle this in a separate effect or manually call the service if we don't want to use the query here
      // But for "cleaner" code, we can use the service directly or a lazy query.
      // Keeping manual call for now to avoid complex effect dependency chains for auto-fill
      const service = new AddressService(country);
      try {
        const address = await service.getLocationByZipcode(value);
        if (address && !address.error) {
          setClient(prev => ({
            ...prev,
            [parent]: {
              ...prev[parent],
              street: address.street,
              neighborhood: address.neighborhood,
              neighborhood_id: address.neighborhood_id,
              city: address.city,
              city_id: address.city_id,
              state: address.state,
              state_id: address.state_id,
              zipcode: value,
            },
          }));
        }
      } catch (err) {
        console.debug('fillAddress error', err);
      }
      return;
    }

    const parsedValue = typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
    setClient(prev => ({ ...prev, [parent]: parsedValue }));
  }, [client, country, validateZipcode]);

  const handleChangeFranchisee = useCallback(f => setSelectedFranchisee(f), []);

  const handleSubmit = useCallback(async evt => {
    evt.preventDefault();

    if (!selectedFranchisee?.value) {
      dispatch(showToast({
        type: 'error',
        title: intl.formatMessage({ id: 'ERROR' }),
        text: intl.formatMessage({ id: 'SELECT_ONE_FRANCHISE_MESSAGE' }),
        duration: 3000,
      }));
      return;
    }

    const payload = {
      ...client,
      franchisee_id: selectedFranchisee.value,
    };
    if (!showBillingAddress) delete payload.billing_address;

    setBusy(true);
    createMutation.mutate(payload, {
      onSuccess: () => {
        navigate('/commercial/clients', { replace: true });
        dispatch(showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'NEW_CUSTOMER_ADDED_MESSAGE' }),
          duration: 3000,
        }));
      },
      onError: (err) => {
        let message;
        if (err.response?.data) {
          const firstKey = Object.keys(err.response.data)[0];
          message = intl.formatMessage({ id: `${firstKey}.${err.response.data[firstKey]}` });
        } else {
          message = intl.formatMessage({ id: 'DATA_SAVE_ERROR' });
        }
        dispatch(showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: message,
          duration: 3000,
        }));
      },
      onSettled: () => setBusy(false)
    });
  }, [client, createMutation, dispatch, intl, navigate, selectedFranchisee, showBillingAddress]);

  // City/Neighborhood options are now handled via separate queries for better caching
  const { data: cities = [] } = useCities(client.address?.state_id);
  const { data: billingCities = [] } = useCities(client.billing_address?.state_id);
  const { data: neighborhoods = [] } = useNeighborhoods(client.address?.city_id);
  const { data: billingNeighborhoods = [] } = useNeighborhoods(client.billing_address?.city_id);

  return (
    <ClientRegistrationFormComponent
      busy={busy || createMutation.isPending}
      client={client}
      franchisees={franchisees}
      loading={loadingFranchisees}
      showBillingAddress={showBillingAddress}
      origins={ORIGINS}
      classifications={CLASSIFICATIONS}
      zipcodeMask={masks.zipcode}
      states={states}
      cities={cities}
      billingCities={billingCities}
      neighborhoods={neighborhoods}
      billingNeighborhoods={billingNeighborhoods}
      type={type}
      intl={intl}
      onChange={handleChange}
      onChangeFranchisee={handleChangeFranchisee}
      onSubmit={handleSubmit}
      cnpjMask={masks.cnpj}
      cpfOnlyMask={masks.cpf}
      phoneMask={masks.phone}
      cellphoneMask={masks.cellphone}
      cellphoneMaskLength={masks.cellphoneLength}
      useTextInputForAddress={useTextInputForAddress}
    />
  );
};

export default ClientRegistrationFormContainer;
